import React, { useState, useEffect } from "react";
import { useAuth } from "@/src/lib/AuthContext";
import { collection, query, where, getDocs, orderBy, doc, getDoc } from "firebase/firestore";
import { db as firestoreDb } from "@/src/lib/firebase";
import { Order, Shop } from "@/src/types";
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Package, Calendar, Store, MessageCircle, ChevronRight } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { getWhatsAppLink } from "@/src/lib/utils";

interface OrderWithShop extends Order {
  shop?: Shop;
}

export function OrdersPage() {
  const { user, isAuthReady } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState<OrderWithShop[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isAuthReady && !user) {
      navigate("/login");
    }
  }, [user, isAuthReady, navigate]);

  useEffect(() => {
    const fetchOrders = async () => {
      if (!user) return;

      try {
        const q = query(
          collection(firestoreDb, "orders"),
          where("buyerUid", "==", user.uid),
          orderBy("createdAt", "desc")
        );
        const querySnapshot = await getDocs(q);
        const ordersData = querySnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id } as Order));
        
        const ordersWithShops: OrderWithShop[] = [];
        for (const order of ordersData) {
          const shopDoc = await getDoc(doc(firestoreDb, "shops", order.shopId));
          ordersWithShops.push({
            ...order,
            shop: shopDoc.exists() ? { ...shopDoc.data(), id: shopDoc.id } as Shop : undefined
          });
        }
        
        setOrders(ordersWithShops);
      } catch (error) {
        console.error("Error fetching orders:", error);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchOrders();
    }
  }, [user]);

  const handleResendWhatsApp = (order: OrderWithShop) => {
    if (!order.shop?.whatsapp) return;

    let message = `*Reenvío de Pedido de EmpreMarket*\n\n`;
    message += `ID del Pedido: ${order.id}\n\n`;
    
    for (const item of order.items) {
      message += `• ${item.name} x${item.quantity} - $${(item.price * item.quantity).toLocaleString()}\n`;
    }

    message += `\n*Total: $${order.total.toLocaleString()}*`;
    message += `\n\n*Pedido realizado por:* ${user?.displayName || "Usuario"}`;
    message += `\n*Email:* ${user?.email}`;

    window.open(getWhatsAppLink(order.shop.whatsapp, message), "_blank");
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-12 flex justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className="container mx-auto px-4 py-12 text-center">
        <div className="max-w-md mx-auto">
          <Package className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
          <h2 className="text-2xl font-bold mb-2">No tienes pedidos aún</h2>
          <p className="text-muted-foreground mb-6">
            Cuando realices un pedido por WhatsApp, aparecerá aquí para que puedas hacerle seguimiento.
          </p>
          <Button asChild>
            <Link to="/products">Explorar Productos</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-8">Mis Pedidos</h1>
      
      <div className="space-y-6">
        {orders.map((order) => (
          <Card key={order.id} className="overflow-hidden">
            <CardHeader className="bg-muted/30 py-4">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <Package className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <CardTitle className="text-base">Pedido #{order.id?.slice(-6)}</CardTitle>
                    <div className="flex items-center text-xs text-muted-foreground gap-2">
                      <Calendar className="h-3 w-3" />
                      {new Date(order.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Badge variant={order.status === "completed" ? "default" : "secondary"}>
                    {order.status === "pending" ? "Pendiente" : 
                     order.status === "completed" ? "Completado" : "Cancelado"}
                  </Badge>
                  <div className="font-bold text-lg">
                    ${order.total.toLocaleString()}
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-4 text-sm font-medium">
                <Store className="h-4 w-4 text-muted-foreground" />
                <span>Tienda:</span>
                <Link to={`/shop/${order.shopId}`} className="text-primary hover:underline">
                  {order.shop?.name || "Tienda Desconocida"}
                </Link>
              </div>
              
              <div className="space-y-3">
                {order.items.map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded bg-muted overflow-hidden">
                        <img 
                          src={item.image || `https://picsum.photos/seed/${item.productId}/100/100`} 
                          alt={item.name}
                          className="h-full w-full object-cover"
                          referrerPolicy="no-referrer"
                        />
                      </div>
                      <span>{item.name} <span className="text-muted-foreground">x{item.quantity}</span></span>
                    </div>
                    <span className="font-medium">${(item.price * item.quantity).toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </CardContent>
            <CardFooter className="border-t p-4 flex justify-between items-center">
              <Button variant="ghost" size="sm" asChild>
                <Link to={`/shop/${order.shopId}`} className="gap-2">
                  Ver Tienda <ChevronRight className="h-4 w-4" />
                </Link>
              </Button>
              {order.shop?.whatsapp && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="text-[#25D366] border-[#25D366] hover:bg-[#25D366]/10 gap-2"
                  onClick={() => handleResendWhatsApp(order)}
                >
                  <MessageCircle className="h-4 w-4" />
                  Contactar Vendedor
                </Button>
              )}
            </CardFooter>
          </Card>
        ))}
      </div>
    </div>
  );
}
